<template>
    <div class="app-container">
      <header-bar></header-bar>
      <div class="content-wrapper">
        <div class="empty-left"></div>
        <div class="left-sidenotes"></div>
        <div class="main">
          <p class="header">Welcome to Ansable VT</p>
          <p>Ansable VT is central Vermont’s community connector, bringing residents of Addison County the latest local news from around the state and updates from their town government.</p>
          <p>In Vermont's small towns, staying informed about local government and community affairs should be simple, but it rarely is. Even in communities of just a few hundred people, participation proves challenging. Weekday meetings stretch for hours, grainy audio and minutes can be hard to parse, and despite excellent regional publications, many small towns are news deserts. Too often, critical information travels through word of mouth rather than reliable channels.</p>
          <p>Ansable VT transforms how Vermonters engage with their local institutions and access community news. We aggregate and promote Vermont's trusted news sources – like the Addison Independent, VTDigger, and more – bringing an overview of their coverage into one streamlined feed while respecting their paywalls and directing readers to their platforms.</p>
          <p>In conjunction, we address the rural reporting gap by providing AI-assisted, editor-verified summaries of local government meetings, making the content of lengthy select board, school board, and town meetings accessible to busy residents. This content is combined with a personalized, location-based approach to civic engagement.</p>
          <p>When residents log in, they see updates relevant to their specific town, including recent meeting summaries, newly posted minutes, and ongoing local discussions. Our searchable database allows citizens to track specific issues over time, from road maintenance projects to school board decisions. Community members can contribute their insights alongside meeting summaries, fostering more informed discussions and a fuller understanding of local issues.</p>
          <p>Small towns run on citizen engagement, but staying informed shouldn't be a burden. Ansable VT distills Vermont's local news and government decisions into clear, accessible updates that keep you connected to what matters in your community.</p>
          
          <p class="header">Our core values</p>
          <p>1. To promote local journalism in Vermont: Ansable VT brings together the day’s news from the Addison Independent, Brandon Reporter, Vermont Public, VTDigger and Seven Days into one streamlined, easy to access feed. Using an AI model trained by an editor, brief summaries of the news story encourage users to click through to read the full story on the news site’s own page. All paywalls are intact and respected.</p>
          <p>2. To fill in the gaps on rural reporting: Democracy functions best with participation from its citizens. News outlets in Addison County and across the state dedicate staff time and resources to effectively cover select board, city council and school board meetings. But given the nature of our state — rural with lots of small municipalities — it’s hard to cover every meeting. It’s even harder for people to find time to attend every meeting, even those that directly impact their lives. Using a custom-built AI model moderated by an experienced editor, Ansable VT provides up-to-date meeting summaries, full minutes and audio access, and a searchable topics database for your town’s select board and school board meetings.</p>
          <p>3. To encourage civic engagement and build community connections: Each Ansable VT user has a homepage personalized based on their location and local media preferences. New meeting summaries are posted right next to the day’s headlines. Using the search function, you can follow along with updates and conversations in your town’s latest meeting. Want to know about the budget for buying a new snow plow? Type it into the search and find out. Even better, did you attend the meeting and want to provide further depth to the summary? Add a comment to help inform your fellow residents.</p>
          <p>4. To support transparency and access to government: Through Ansable VT, towns can foster a more informed citizenry. Easier access to meetings, using Ansable VT’s meeting summary tool, lessens the time barrier many people face when trying to be more involved with their local government.</p>
          
          <p class="header">About the founders</p>
          <p>Mike Medeiros and Allie Ginwala moved to Vermont in 2020. Born and raised in New Hampshire, the pair spent several years in New York City before settling in Goshen with their two cats, Neko and Moshi. As an avid backcountry skier and competitive hiker and runner (Mike) and a lover of walks in the woods, trips to the lake, and maple lattes (Allie) Addison County was the perfect place to call home.</p>
          <p>While they come from different professional backgrounds, Allie and Mike align on their passion for building community. Allie has spent the last 11 years in local journalism. She’s written for a number of publications, including The Hippo and Keene Sentinel, and worked for the Brooklyn Public Library as the coordinator for The Open Newsroom, an initiative to make local news more collaborative. Most recently she was audience engagement editor for the Concord Monitor, managing the opinion section and leading engagement, trust and transparency initiatives across the company.</p>
          <p>Mike specializes in the intersection of technology, operations, and community. After starting his career as a grant trainee at the United Nations, Mike has spent time as a fundraiser and chief-of-staff for a number of causes. He currently works remotely for Bridgewater Associates as the Technology Engagement Manager, where he oversees communications, community building, onboarding, learning and development, and other employee retention programs. Over his time at Bridgewater, he has also led a number of technical implementations and projects related to his remit.</p>
          
          <p class="header">How we’re funded</p>
          <p>Ansable VT is currently fully funded by its founders. To help us grow in the future, we’ll explore different options such as individual memberships, advertising and business underwriting, foundation grants, and private contributions. We’ll keep you posted and update this page as our funding model evolves.</p>
          <p>Interested in supporting Ansable VT? Reach out to Ansablevt@gmail.com.</p>
        </div>
        <div class="right-sidenotes"></div>
      </div>
      <footer-bar></footer-bar>
    </div>
  </template>
  
  <script>
  import HeaderBar from "./RevisedHeader.vue";
  import FooterBar from "./FooterBar.vue";
  
  export default {
    name: "AboutUs",
    components: {
      HeaderBar,
      FooterBar,
    },
  };
  </script>
  
  <style scoped>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html,
  body {
    height: 100%;
  }
  
  .app-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    overflow: hidden;
  }
  
  .content-wrapper {
    position: relative;
    flex: 1;
    display: flex;
    overflow: hidden;
    background-color: #fffff8;
    padding-top: 64px;
  }
  
  .empty-left {
    flex: 1;
    background-color: #fffff8;
  }
  
  .left-sidenotes,
  .right-sidenotes {
    flex: 1;
    background-color: #fffff8;
  }
  
  .main {
    flex: 3;
    background-color: #fffff8;
    display: flex;
    flex-direction: column;
    overflow: auto;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  .header {
    font-weight: bold;
    margin-bottom: 0rem;
    margin-top: 2rem;
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  p{
    margin-bottom: 1rem;
  }
  
  /* Footer styling */
  footer-bar {
    display: block;
    width: 100%;
    background-color: #545051;
    color: white;
    padding: 16px;
    text-align: center;
    font-size: 0.9rem;
  }
  
  /* Responsive design */
  @media (max-width: 768px) {
    .empty-left,
    .left-sidenotes,
    .right-sidenotes {
      display: none;
    }
    .main {
      flex: 1;
      max-width: 100%;
      padding: 0 1rem;
    }
  }
  
  @media (min-width: 1024px) {
    .empty-left,
    .left-sidenotes,
    .right-sidenotes {
      display: flex;
    }
    .main {
      flex: 3;
      max-width: 1200px;
      padding: 0;
    }
  }
  </style>
  